/**
 * Is this session's agent busy, and which run is it (RFC 0269).
 *
 * Every surface needs this and none of them had it, so each was left inferring
 * "the agent is working" from whether tokens happened to be arriving. That is
 * wrong in both directions: a run that is queued with no worker present looks
 * idle (so the composer offers to send again, stacking a second run), and a run
 * whose worker died mid-turn looks busy forever.
 *
 * Reading the run's own status instead makes the answer the log's, not the
 * renderer's, so every surface agrees — the composer can show STOP while the
 * agent works, and offer SEND again the moment it stops, on web, desktop, and
 * iOS alike.
 */

import { topoOrder } from './projection-order'
import type { EntryId, LogDoc, RunData, RunStatus } from './types'

/** Run states that mean "the agent has not finished with this turn". */
const ACTIVE: ReadonlySet<RunStatus> = new Set<RunStatus>([
  'queued',
  'running',
  'waiting_for_tools',
])

export interface ActiveRun {
  runId: EntryId
  status: RunStatus
  agent: string
  /** True once a worker has claimed it; false while it waits for one. The
   *  composer can distinguish "working" from "waiting for a worker", which is
   *  the difference between a slow agent and no agent at all. */
  claimed: boolean
  /** World skills this run was asked for (RFC 0283 S5), by catalog id. Empty
   *  when none were, which is every run written before the field existed. */
  skills: string[]
  /** Composer selection, when present — provider is the harness id. */
  requestedModel?: { provider: string; model: string }
}

/**
 * The session's active run, or null when the agent is idle.
 *
 * Takes the LAST active run in topological order: a session can hold many runs
 * over its life, and only the most recent can be in flight.
 */
export function projectActiveRun(doc: LogDoc): ActiveRun | null {
  return projectActiveRuns(doc)[0] ?? null
}

/**
 * EVERY active run, oldest first.
 *
 * Plural because a session can genuinely hold more than one: nothing stops a
 * user sending a follow-up while a run is in flight, and `enqueueRun` does not
 * inspect run state. Reporting only one made Stop cancel the wrong run — the
 * newest queued one — while the run actually burning tokens carried on.
 *
 * Oldest first because that is the one being worked; the composer shows its
 * state, and cancellation starts there.
 */
export function projectActiveRuns(doc: LogDoc): ActiveRun[] {
  const out: ActiveRun[] = []
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'run') continue
    const run = p.payload as RunData
    if (!ACTIVE.has(run.status)) continue
    out.push({
      runId: e.id,
      status: run.status,
      agent: run.agent,
      claimed: run.status !== 'queued',
      skills: Array.isArray(run.skills) ? run.skills.map(String) : [],
      ...(run.requestedModel &&
      typeof run.requestedModel === 'object' &&
      typeof (run.requestedModel as { provider?: unknown }).provider ===
        'string' &&
      typeof (run.requestedModel as { model?: unknown }).model === 'string'
        ? {
            requestedModel: {
              provider: String(
                (run.requestedModel as { provider: string }).provider,
              ),
              model: String((run.requestedModel as { model: string }).model),
            },
          }
        : {}),
    })
  }
  return out
}

/** Convenience: is the agent busy on this session? */
export function isAgentBusy(doc: LogDoc): boolean {
  return projectActiveRun(doc) !== null
}
